import { useState, useEffect } from 'react';
import { useColumnContextAPI } from '../context/GetAllColumnContext';
import { useConfigContext } from '../context/ConfigContext';
import { getApiBaseUrl } from './apiBase';

const useGridColumnAccess = (props) => {
  const { tableRes, roleIds } = props;
  const { mbdDataGrid, id: tableId } = tableRes;
  const { updateTableDetails, updateAccessDetails, setStateAllColumnData } = useColumnContextAPI();
  const [isColumnConfigLoading, setIsColumnConfigLoading] = useState(true);
  const { configSchema } = useConfigContext();
  const [isError, setIsError] = useState(false);
  const apiBase = getApiBaseUrl();

  useEffect(() => {
    const fetchGridAccessData = async () => {
      try {
        setIsColumnConfigLoading(true);
        updateTableDetails(tableRes);

        const [featureDetailsRes, attributeDetailsRes] = await Promise.all([
          fetch(`${apiBase}/api/screenGridFeatureDetails?screenGridId=${tableId}`).then((res) =>
            res.json(),
          ),
          fetch(`${apiBase}/api/screenGridAttributeDetails?screenGridId=${tableId}`).then((res) =>
            res.json(),
          ),
        ]);

        const featureDetails =
          featureDetailsRes?.[`${configSchema}_mbd_screen_grid_feature`]?.map((item) => item.id) ||
          [];
        const attributeDetails =
          attributeDetailsRes?.[`${configSchema}_mbd_screen_grid_attribute_config`]?.map(
            (item) => item.id,
          ) || [];

        const queryParamsForFeatures = new URLSearchParams();
        roleIds.forEach((id) => queryParamsForFeatures.append('roleId', id));
        featureDetails.forEach((id) => queryParamsForFeatures.append('screenGridFeatureId', id));

        const queryParamsForAttributes = new URLSearchParams();
        roleIds.forEach((id) => queryParamsForAttributes.append('roleId', id));
        attributeDetails.forEach((id) => queryParamsForAttributes.append('screenGridAttrId', id));

        const [featureAccessRes, attributeAccessRes] = await Promise.all([
          fetch(`${apiBase}/api/screenGridFeatureAccess?${queryParamsForFeatures}`).then((res) =>
            res.json(),
          ),
          fetch(`${apiBase}/api/screenGridAttributeAccess?${queryParamsForAttributes}`).then(
            (res) => res.json(),
          ),
        ]);

        const featureAccess = extractFeatureAccess(
          featureAccessRes[`${configSchema}_mbd_roles_to_screen_grid_features`],
        );
        const attributeAccess = extractAttributeAccess(
          attributeAccessRes[`${configSchema}_mbd_roles_to_screen_grid_attribute`],
        );

        const mergedAccess = mergeAccessData(featureAccess, attributeAccess);
        updateAccessDetails({ ...mergedAccess, ...mbdDataGrid });

        const columnData = await fetch(
          `${apiBase}/api/screenColumnData?` +
            new URLSearchParams({
              screenGridId: tableId,
            }),
        ).then((res) => res.json());

        if (columnData?.[`${configSchema}_mbd_screen_grid_attribute_config`]?.length) {
          setStateAllColumnData(columnData[`${configSchema}_mbd_screen_grid_attribute_config`]);
        } else {
          setIsError(true);
        }
      } catch (error) {
        console.error(error);
        setIsError(true);
      } finally {
        setIsColumnConfigLoading(false);
      }
    };

    fetchGridAccessData();
  }, []);

  return { isColumnConfigLoading, isError };
};

const extractFeatureAccess = (featureAccess) =>
  featureAccess?.map(({ mbdScreenGridFeature, accessLevel }) => ({
    accessLevel,
    featureName: mbdScreenGridFeature.mbdFeatureConfig.featureName,
    screenGridId: mbdScreenGridFeature.screenGridId,
    sortOrderNo: mbdScreenGridFeature.sortOrderNo,
  })) || [];

const extractAttributeAccess = (attributeAccess) =>
  attributeAccess?.map(({ mbdScreenGridAttrConfig, accessLevel }) => ({
    accessLevel,
    featureName: mbdScreenGridAttrConfig.attrInternalName,
    screenGridId: mbdScreenGridAttrConfig.screenGridId,
  })) || [];

const mergeAccessData = (featureAccess, attributeAccess) => {
  return [...featureAccess, ...attributeAccess].reduce((acc, item) => {
    const key = item.featureName.trim();
    if (!acc[key] || item.accessLevel === 'U' || item.accessLevel === 'S') {
      acc[key] = item;
    }
    return acc;
  }, {});
};

export default useGridColumnAccess;
